import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchDteCaf,
  getDteCafAudits,
  getDteSettings,
  lookupDteTaxpayer,
  resetDteCafNextFolio,
  storeDteApiKey,
  updateDteCafStatus,
  updateDteAutomation,
  updateDteBranchProfile,
  updateDteCompanyProfile,
  uploadDteCaf,
  uploadDteCertificate,
} from '../api';
import InlineLoading from './InlineLoading';

const DOCUMENT_LABELS = {
  boleta: 'Boleta',
  factura: 'Factura',
  guia: 'Guia de despacho',
};

const DTE_TYPE_LABELS = {
  33: 'Factura afecta',
  39: 'Boleta afecta',
  52: 'Guia de despacho',
};

const emptyProfileForm = () => ({
  environment: 'certification',
  salesBaseUrl: 'https://api.simpleapi.cl',
  servicesBaseUrl: 'https://servicios.simpleapi.cl',
  rutBaseUrl: 'https://rut.simpleapi.cl',
  issuerRut: '',
  issuerRazonSocial: '',
  issuerGiro: '',
  activityCodes: '',
  resolutionDate: '',
  resolutionNumber: '',
  siiUnit: '',
  genericBoletaRut: '66666666-6',
  genericBoletaRazonSocial: 'Cliente no identificado',
  genericBoletaAddress: 'Sin direccion',
  genericBoletaComuna: 'Santiago',
});

const buildProfileForm = (profile) => ({
  ...emptyProfileForm(),
  ...(profile || {}),
  activityCodes: Array.isArray(profile?.activityCodes) ? profile.activityCodes.join(', ') : '',
  resolutionNumber: profile?.resolutionNumber ?? '',
});

const DteSettingsPanel = ({ sectionId, branches = [], canManage = false }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [profileForm, setProfileForm] = useState(emptyProfileForm);
  const [branchForms, setBranchForms] = useState({});
  const [apiKey, setApiKey] = useState('');
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificateRut, setCertificateRut] = useState('');
  const [certificatePassword, setCertificatePassword] = useState('');
  const [cafFile, setCafFile] = useState(null);
  const [cafFetchForm, setCafFetchForm] = useState({ dteType: '39', quantity: '1' });
  const [lookupRut, setLookupRut] = useState('');
  const [automation, setAutomation] = useState({ boleta: 'manual', factura: 'manual', guia: 'manual' });
  const [cafAudits, setCafAudits] = useState({});
  const [auditLoadingId, setAuditLoadingId] = useState(null);

  const loadSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getDteSettings();
      setSettings(data);
      setProfileForm(buildProfileForm(data.profile));
      setCertificateRut(data.profile?.certificateRut || '');
      setAutomation({
        boleta: data.profile?.automation?.boleta || 'manual',
        factura: data.profile?.automation?.factura || 'manual',
        guia: data.profile?.automation?.guia || 'manual',
      });
      const nextBranchForms = {};
      (data.branches || []).forEach((branch) => {
        nextBranchForms[String(branch.branchId)] = {
          enabled: Boolean(branch.enabled),
          address: branch.address || '',
          comuna: branch.comuna || '',
          city: branch.city || '',
          phone: branch.phone || '',
          siiBranchCode: branch.siiBranchCode || '',
        };
      });
      branches.forEach((branch) => {
        const key = String(branch.id);
        if (!nextBranchForms[key]) {
          nextBranchForms[key] = {
            enabled: false,
            address: '',
            comuna: '',
            city: '',
            phone: '',
            siiBranchCode: '',
          };
        }
      });
      setBranchForms(nextBranchForms);
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo cargar la configuracion DTE.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const profile = settings?.profile;
  const cafs = settings?.cafs || [];
  const branchRows = useMemo(() => {
    const byId = new Map((settings?.branches || []).map((entry) => [String(entry.branchId), entry]));
    return branches.map((branch) => ({
      ...branch,
      dte: byId.get(String(branch.id)) || {},
    }));
  }, [branches, settings]);

  const runSave = async (action, successMessage) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await action();
      setMessage(successMessage);
      await loadSettings();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = () => runSave(
    () => updateDteCompanyProfile({
      ...profileForm,
      activityCodes: profileForm.activityCodes,
      resolutionNumber: profileForm.resolutionNumber === '' ? null : Number(profileForm.resolutionNumber),
    }),
    'Perfil emisor guardado.'
  );

  const handleImportIssuerFromRut = async () => {
    if (!lookupRut && !profileForm.issuerRut) return;
    setSaving(true);
    setError('');
    try {
      const response = await lookupDteTaxpayer({ rut: lookupRut || profileForm.issuerRut });
      const taxpayer = response?.taxpayer || response || {};
      const activities = Array.isArray(taxpayer.actividadesEconomicas) ? taxpayer.actividadesEconomicas : [];
      const firstActivity = activities[0] || {};
      setProfileForm((prev) => ({
        ...prev,
        issuerRut: taxpayer.rut || lookupRut || prev.issuerRut,
        issuerRazonSocial: taxpayer.razonSocial || taxpayer.nombre || prev.issuerRazonSocial,
        issuerGiro: firstActivity.giro || firstActivity.actividad || firstActivity.descripcion || prev.issuerGiro,
        activityCodes: activities
          .map((activity) => activity.codigo || activity.codigoActividad || activity.actividadEconomica)
          .filter(Boolean)
          .join(', '),
      }));
      setMessage('Datos importados desde SimpleAPI RUT.');
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo consultar el RUT.');
    } finally {
      setSaving(false);
    }
  };

  const handleBranchChange = (branchId, field, value) => {
    setBranchForms((prev) => ({
      ...prev,
      [String(branchId)]: {
        ...(prev[String(branchId)] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveBranch = (branchId) => runSave(
    () => updateDteBranchProfile(branchId, branchForms[String(branchId)] || {}),
    'Sucursal fiscal guardada.'
  );

  const handleSaveApiKey = () => runSave(
    () => storeDteApiKey(apiKey, profileForm.environment),
    'API key guardada.'
  );

  const handleSaveCertificate = () => runSave(
    () => uploadDteCertificate({
      file: certificateFile,
      certificateRut,
      password: certificatePassword,
      environment: profileForm.environment,
    }),
    'Certificado guardado.'
  );

  const handleUploadCaf = () => runSave(
    () => uploadDteCaf({ file: cafFile, environment: profileForm.environment }),
    'CAF cargado.'
  );

  const handleFetchCaf = () => runSave(
    () => fetchDteCaf({
      dteType: cafFetchForm.dteType,
      quantity: cafFetchForm.quantity,
      environment: profileForm.environment,
    }),
    'CAF solicitado a SimpleAPI Folios.'
  );

  const handleSaveAutomation = () => runSave(
    () => updateDteAutomation(automation, profileForm.environment),
    'Automatizacion guardada.'
  );

  const handleToggleCaf = (caf) => runSave(
    () => updateDteCafStatus(caf.id, {
      status: caf.status === 'disabled' ? 'active' : 'disabled',
      reason: caf.status === 'disabled' ? null : 'Deshabilitado desde configuracion DTE',
    }),
    caf.status === 'disabled' ? 'CAF activado.' : 'CAF deshabilitado.'
  );

  const handleResetCafFolio = (caf) => {
    const nextFolio = window.prompt('Nuevo proximo folio', String(caf.nextFolio || caf.folioStart));
    if (!nextFolio) return;
    runSave(
      () => resetDteCafNextFolio(caf.id, nextFolio),
      'Proximo folio actualizado.'
    );
  };

  const handleLoadCafAudit = async (cafId) => {
    if (cafAudits[cafId]) {
      setCafAudits((prev) => {
        const next = { ...prev };
        delete next[cafId];
        return next;
      });
      return;
    }
    setAuditLoadingId(cafId);
    setError('');
    try {
      const response = await getDteCafAudits(cafId);
      setCafAudits((prev) => ({ ...prev, [cafId]: response.audits || [] }));
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo cargar la auditoria CAF.');
    } finally {
      setAuditLoadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <InlineLoading label="Cargando configuracion DTE" />
      </div>
    );
  }

  const renderStatus = () => (
    <div className="mb-4 space-y-2">
      {message && <p className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{message}</p>}
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
      {!canManage && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
          No tienes permisos para modificar configuracion DTE.
        </p>
      )}
    </div>
  );

  const renderProfile = () => (
    <div className="space-y-4">
      {renderStatus()}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="text-sm">
          <span className="font-medium text-gray-700">Ambiente</span>
          <select
            value={profileForm.environment}
            onChange={(event) => handleProfileChange('environment', event.target.value)}
            disabled={!canManage || saving}
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="certification">Certificacion</option>
            <option value="production">Produccion</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="font-medium text-gray-700">RUT emisor</span>
          <input value={profileForm.issuerRut || ''} onChange={(event) => handleProfileChange('issuerRut', event.target.value)} disabled={!canManage || saving} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm md:col-span-2">
          <span className="font-medium text-gray-700">Razon social</span>
          <input maxLength={100} value={profileForm.issuerRazonSocial || ''} onChange={(event) => handleProfileChange('issuerRazonSocial', event.target.value)} disabled={!canManage || saving} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm md:col-span-2">
          <span className="font-medium text-gray-700">Giro emisor</span>
          <input maxLength={80} value={profileForm.issuerGiro || ''} onChange={(event) => handleProfileChange('issuerGiro', event.target.value)} disabled={!canManage || saving} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="font-medium text-gray-700">Codigos de actividad</span>
          <input value={profileForm.activityCodes || ''} onChange={(event) => handleProfileChange('activityCodes', event.target.value)} disabled={!canManage || saving} placeholder="620200, 631100" className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="font-medium text-gray-700">Unidad SII</span>
          <input value={profileForm.siiUnit || ''} onChange={(event) => handleProfileChange('siiUnit', event.target.value)} disabled={!canManage || saving} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="font-medium text-gray-700">Fecha resolucion</span>
          <input type="date" value={profileForm.resolutionDate || ''} onChange={(event) => handleProfileChange('resolutionDate', event.target.value)} disabled={!canManage || saving} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="font-medium text-gray-700">Numero resolucion</span>
          <input type="number" value={profileForm.resolutionNumber || ''} onChange={(event) => handleProfileChange('resolutionNumber', event.target.value)} disabled={!canManage || saving} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="font-medium text-gray-700">RUT boleta generica</span>
          <input value={profileForm.genericBoletaRut || ''} onChange={(event) => handleProfileChange('genericBoletaRut', event.target.value)} disabled={!canManage || saving} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="font-medium text-gray-700">Razon social boleta generica</span>
          <input maxLength={100} value={profileForm.genericBoletaRazonSocial || ''} onChange={(event) => handleProfileChange('genericBoletaRazonSocial', event.target.value)} disabled={!canManage || saving} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="font-medium text-gray-700">Direccion boleta generica</span>
          <input maxLength={70} value={profileForm.genericBoletaAddress || ''} onChange={(event) => handleProfileChange('genericBoletaAddress', event.target.value)} disabled={!canManage || saving} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="font-medium text-gray-700">Comuna boleta generica</span>
          <input maxLength={20} value={profileForm.genericBoletaComuna || ''} onChange={(event) => handleProfileChange('genericBoletaComuna', event.target.value)} disabled={!canManage || saving} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={lookupRut} onChange={(event) => setLookupRut(event.target.value)} placeholder="RUT a consultar" disabled={!canManage || saving} className="border border-gray-300 rounded-lg px-3 py-2" />
        <button type="button" onClick={handleImportIssuerFromRut} disabled={!canManage || saving} className="px-4 py-2 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-60">
          Importar desde SII
        </button>
        <button type="button" onClick={handleSaveProfile} disabled={!canManage || saving} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
          {saving ? 'Guardando...' : 'Guardar perfil'}
        </button>
      </div>
    </div>
  );

  const renderCredentials = () => (
    <div className="space-y-5">
      {renderStatus()}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="font-semibold text-gray-800">API key</div>
          <div className={profile?.secrets?.apiKeyConfigured ? 'text-green-700' : 'text-red-700'}>
            {profile?.secrets?.apiKeyConfigured ? 'Configurada' : 'Pendiente'}
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="font-semibold text-gray-800">PFX</div>
          <div className={profile?.secrets?.pfxConfigured ? 'text-green-700' : 'text-red-700'}>
            {profile?.secrets?.pfxConfigured ? profile?.secrets?.pfxFileName || 'Configurado' : 'Pendiente'}
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="font-semibold text-gray-800">Clave env</div>
          <div className={profile?.secrets?.encryptionConfigured ? 'text-green-700' : 'text-red-700'}>
            {profile?.secrets?.encryptionConfigured ? 'DTE_ENCRYPTION_KEY OK' : 'Falta DTE_ENCRYPTION_KEY'}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-gray-800">SimpleAPI</h3>
          <input type="password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder="Nueva API key" disabled={!canManage || saving} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          <button type="button" onClick={handleSaveApiKey} disabled={!canManage || saving || !apiKey} className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-60">
            Guardar API key
          </button>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-gray-800">Certificado PFX</h3>
          <input value={certificateRut} onChange={(event) => setCertificateRut(event.target.value)} placeholder="RUT certificado" disabled={!canManage || saving} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          <input type="password" value={certificatePassword} onChange={(event) => setCertificatePassword(event.target.value)} placeholder="Password certificado" disabled={!canManage || saving} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          <input type="file" accept=".pfx,.p12" onChange={(event) => setCertificateFile(event.target.files?.[0] || null)} disabled={!canManage || saving} className="w-full text-sm" />
          <button type="button" onClick={handleSaveCertificate} disabled={!canManage || saving || !certificateFile || !certificatePassword || !certificateRut} className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-60">
            Guardar certificado
          </button>
        </div>
      </div>
    </div>
  );

  const renderBranches = () => (
    <div className="space-y-4">
      {renderStatus()}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Sucursal</th>
              <th className="px-3 py-2 text-left">Activa</th>
              <th className="px-3 py-2 text-left">Direccion</th>
              <th className="px-3 py-2 text-left">Comuna</th>
              <th className="px-3 py-2 text-left">Ciudad</th>
              <th className="px-3 py-2 text-left">Telefono</th>
              <th className="px-3 py-2 text-left">Codigo SII</th>
              <th className="px-3 py-2 text-right">Accion</th>
            </tr>
          </thead>
          <tbody>
            {branchRows.map((branch) => {
              const form = branchForms[String(branch.id)] || {};
              return (
                <tr key={branch.id} className="border-t align-top">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-gray-800">{branch.branchName}</div>
                    {branch.dte?.missing?.length > 0 && (
                      <div className="text-xs text-red-600">{branch.dte.missing.join(', ')}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={Boolean(form.enabled)} onChange={(event) => handleBranchChange(branch.id, 'enabled', event.target.checked)} disabled={!canManage || saving} />
                  </td>
                  {['address', 'comuna', 'city', 'phone', 'siiBranchCode'].map((field) => (
                    <td key={field} className="px-3 py-2">
                      <input value={form[field] || ''} onChange={(event) => handleBranchChange(branch.id, field, event.target.value)} disabled={!canManage || saving} maxLength={field === 'address' ? 70 : field === 'comuna' ? 20 : undefined} className="w-36 border border-gray-300 rounded px-2 py-1" />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => handleSaveBranch(branch.id)} disabled={!canManage || saving} className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs disabled:opacity-60">
                      Guardar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCafs = () => (
    <div className="space-y-5">
      {renderStatus()}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-gray-800">Subir CAF XML</h3>
          <input type="file" accept=".xml,text/xml,application/xml" onChange={(event) => setCafFile(event.target.files?.[0] || null)} disabled={!canManage || saving} className="w-full text-sm" />
          <button type="button" onClick={handleUploadCaf} disabled={!canManage || saving || !cafFile} className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-60">
            Subir CAF
          </button>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-gray-800">Solicitar SimpleAPI Folios</h3>
          <select value={cafFetchForm.dteType} onChange={(event) => setCafFetchForm((prev) => ({ ...prev, dteType: event.target.value }))} disabled={!canManage || saving} className="w-full border border-gray-300 rounded-lg px-3 py-2">
            <option value="39">Boleta afecta 39</option>
            <option value="33">Factura afecta 33</option>
            <option value="52">Guia despacho 52</option>
          </select>
          <input type="number" min="1" value={cafFetchForm.quantity} onChange={(event) => setCafFetchForm((prev) => ({ ...prev, quantity: event.target.value }))} disabled={!canManage || saving} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          <button type="button" onClick={handleFetchCaf} disabled={!canManage || saving} className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-60">
            Solicitar CAF
          </button>
        </div>
      </div>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Rango</th>
              <th className="px-3 py-2 text-left">Proximo</th>
              <th className="px-3 py-2 text-left">Restantes</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Origen</th>
              <th className="px-3 py-2 text-left">Hash</th>
              <th className="px-3 py-2 text-left">Validacion</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cafs.map((caf) => (
              <React.Fragment key={caf.id}>
                <tr className="border-t">
                  <td className="px-3 py-2">{DTE_TYPE_LABELS[caf.dteType] || caf.dteType}</td>
                  <td className="px-3 py-2">{caf.folioStart} - {caf.folioEnd}</td>
                  <td className="px-3 py-2">{caf.nextFolio}</td>
                  <td className={`px-3 py-2 ${caf.remainingFolios <= 10 && caf.status === 'active' ? 'text-amber-700 font-semibold' : ''}`}>{caf.remainingFolios}</td>
                  <td className="px-3 py-2">{caf.status}</td>
                  <td className="px-3 py-2">{caf.source}</td>
                  <td className="px-3 py-2 font-mono text-xs">{caf.sha256?.slice(0, 12)}</td>
                  <td className="px-3 py-2 text-xs">
                    {caf.validationErrors?.length > 0 ? (
                      <span className="text-red-700">{caf.validationErrors.join(', ')}</span>
                    ) : caf.validationWarnings?.length > 0 ? (
                      <span className="text-amber-700">{caf.validationWarnings.join(', ')}</span>
                    ) : (
                      <span className="text-green-700">OK</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button type="button" onClick={() => handleLoadCafAudit(caf.id)} disabled={!canManage || auditLoadingId === caf.id} className="px-2 py-1 rounded border border-gray-300 text-xs disabled:opacity-60">
                        {cafAudits[caf.id] ? 'Ocultar auditoria' : 'Auditoria'}
                      </button>
                      <button type="button" onClick={() => handleToggleCaf(caf)} disabled={!canManage || saving || caf.status === 'exhausted'} className="px-2 py-1 rounded border border-gray-300 text-xs disabled:opacity-60">
                        {caf.status === 'disabled' ? 'Activar' : 'Deshabilitar'}
                      </button>
                      <button type="button" onClick={() => handleResetCafFolio(caf)} disabled={!canManage || saving} className="px-2 py-1 rounded border border-gray-300 text-xs disabled:opacity-60">
                        Ajustar folio
                      </button>
                    </div>
                  </td>
                </tr>
                {cafAudits[caf.id] && (
                  <tr className="border-t bg-gray-50">
                    <td colSpan={9} className="px-3 py-3">
                      <div className="text-xs text-gray-700 space-y-2">
                        {cafAudits[caf.id].length === 0 ? (
                          <p>No hay eventos de auditoria.</p>
                        ) : cafAudits[caf.id].map((audit) => (
                          <div key={audit.id} className="border border-gray-200 rounded p-2 bg-white">
                            <div className="font-semibold">
                              {audit.action} - {new Date(audit.createdAt).toLocaleString()}
                              {audit.userName ? ` - ${audit.userName}` : ''}
                            </div>
                            {audit.reason && <div>Motivo: {audit.reason}</div>}
                            <div className="font-mono text-[11px] text-gray-500">
                              {audit.previousValues ? `Antes ${JSON.stringify(audit.previousValues)} ` : ''}
                              {audit.nextValues ? `Despues ${JSON.stringify(audit.nextValues)}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {cafs.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-center text-gray-500">No hay CAF cargados.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAutomation = () => (
    <div className="space-y-4">
      {renderStatus()}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.keys(DOCUMENT_LABELS).map((kind) => (
          <label key={kind} className="text-sm border border-gray-200 rounded-lg p-4">
            <span className="font-semibold text-gray-800">{DOCUMENT_LABELS[kind]}</span>
            <select value={automation[kind]} onChange={(event) => setAutomation((prev) => ({ ...prev, [kind]: event.target.value }))} disabled={!canManage || saving} className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2">
              <option value="manual">Manual</option>
              <option value="automatic">Automatica</option>
            </select>
          </label>
        ))}
      </div>
      <button type="button" onClick={handleSaveAutomation} disabled={!canManage || saving} className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-60">
        Guardar automatizacion
      </button>
    </div>
  );

  switch (sectionId) {
    case 'dte-credentials':
      return renderCredentials();
    case 'dte-branches':
      return renderBranches();
    case 'dte-cafs':
      return renderCafs();
    case 'dte-automation':
      return renderAutomation();
    case 'dte-company':
    default:
      return renderProfile();
  }
};

export default DteSettingsPanel;
